import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Loader2, RefreshCw, Trash2, Building2, Wallet, CreditCard, TrendingUp, Landmark, PlusCircle } from "lucide-react";
import PlaidConnect from "./PlaidConnect";
import { toast } from "sonner";

const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const timeAgo = (iso, language) => {
    if (!iso) return language === "es" ? "Nunca" : "Never";
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.round(diff / 60000);
    if (mins < 1) return language === "es" ? "hace un momento" : "just now";
    if (mins < 60) return language === "es" ? `hace ${mins} min` : `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return language === "es" ? `hace ${hrs} h` : `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return language === "es" ? `hace ${days} d` : `${days}d ago`;
};

const accountIcon = (type) => {
    if (type === "credit") return CreditCard;
    if (type === "investment" || type === "brokerage") return TrendingUp;
    if (type === "loan") return Landmark;
    return Wallet;
};

const ConnectedAccounts = () => {
    const { language } = useApp();
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(null); // itemId currently being synced/removed

    const load = async () => {
        try {
            const res = await api.get("/plaid/items");
            setItems(res.data);
        } catch (e) {
            setItems([]);
        }
    };
    useEffect(() => { load(); }, []);

    const syncItem = async (itemId) => {
        setBusy(itemId + ":sync");
        try {
            await api.post(`/plaid/items/${itemId}/sync`);
            toast.success(language === "es" ? "Sincronizado" : "Synced");
            await load();
        } catch (e) {
            toast.error(language === "es" ? "Error al sincronizar" : "Sync failed");
        } finally {
            setBusy(null);
        }
    };

    const removeItem = async (itemId, name) => {
        const confirmMsg = language === "es"
            ? `¿Desconectar ${name}? Se borrarán sus cuentas y transacciones.`
            : `Disconnect ${name}? Its accounts and transactions will be removed.`;
        if (!window.confirm(confirmMsg)) return;
        setBusy(itemId + ":remove");
        try {
            await api.delete(`/plaid/items/${itemId}`);
            toast.success(language === "es" ? "Cuenta desconectada" : "Disconnected");
            await load();
        } catch (e) {
            toast.error(language === "es" ? "Error al desconectar" : "Failed to disconnect");
        } finally {
            setBusy(null);
        }
    };

    if (items === null) {
        return (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-6" data-testid="connected-loading">
                <Loader2 className="w-4 h-4 animate-spin" />
                {language === "es" ? "Cargando cuentas conectadas..." : "Loading connected accounts..."}
            </div>
        );
    }

    return (
        <div data-testid="connected-accounts">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                        {language === "es" ? "INSTITUCIONES · CONECTADAS" : "CONNECTED · INSTITUTIONS"}
                    </div>
                    <div className="text-sm text-[hsl(var(--muted-foreground))]">
                        {language === "es"
                            ? `${items.length} banco${items.length === 1 ? "" : "s"} vinculado${items.length === 1 ? "" : "s"}`
                            : `${items.length} bank${items.length === 1 ? "" : "s"} linked`}
                    </div>
                </div>
                <PlaidConnect onConnected={load} />
            </div>

            {items.length === 0 && (
                <Card className="rounded-2xl border-dashed border-2 border-[hsl(var(--border))] bg-transparent">
                    <CardContent className="p-8 text-center">
                        <Building2 className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--muted-foreground))]" />
                        <div className="font-semibold">
                            {language === "es" ? "Aún no hay bancos conectados" : "No banks connected yet"}
                        </div>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                            {language === "es"
                                ? "Conecta tu primer banco para ver todos tus movimientos en un solo lugar."
                                : "Connect your first bank to see everything in one place."}
                        </p>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-4">
                <AnimatePresence>
                    {items.map((it) => (
                        <motion.div
                            key={it.item_id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.35 }}
                            data-testid={`connected-item-${it.item_id}`}
                        >
                            <Card className="rounded-2xl bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                <CardContent className="p-5">
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                                                <Building2 className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-display font-bold text-lg">
                                                    {it.institution_name || "Bank"}
                                                </div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                                    {(it.accounts || []).length} {language === "es" ? "cuentas" : "accounts"}
                                                    {" · "}
                                                    {language === "es" ? "sincronizado" : "synced"} {timeAgo(it.last_synced_at, language)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                data-testid={`sync-item-${it.item_id}`}
                                                size="sm"
                                                variant="outline"
                                                className="pill gap-2"
                                                onClick={() => syncItem(it.item_id)}
                                                disabled={busy === it.item_id + ":sync"}
                                            >
                                                {busy === it.item_id + ":sync"
                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                    : <RefreshCw className="w-4 h-4" />}
                                                {language === "es" ? "Sincronizar" : "Sync"}
                                            </Button>
                                            <Button
                                                data-testid={`remove-item-${it.item_id}`}
                                                size="sm"
                                                variant="outline"
                                                className="pill gap-2 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                                                onClick={() => removeItem(it.item_id, it.institution_name || "Bank")}
                                                disabled={busy === it.item_id + ":remove"}
                                            >
                                                {busy === it.item_id + ":remove"
                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                    : <Trash2 className="w-4 h-4" />}
                                                {language === "es" ? "Desconectar" : "Disconnect"}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Accounts inside this institution */}
                                    {(it.accounts || []).length > 0 && (
                                        <div className="mt-4 divide-y divide-[hsl(var(--border))]">
                                            {(it.accounts || []).map((a) => {
                                                const Ico = accountIcon(a.type);
                                                const bal = (a.balances || {}).current;
                                                const isCredit = a.type === "credit";
                                                return (
                                                    <div key={a.account_id} className="flex items-center justify-between py-2.5 text-sm">
                                                        <div className="flex items-center gap-3">
                                                            <Ico className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                                                            <div>
                                                                <div className="font-medium">
                                                                    {a.name} <span className="text-[hsl(var(--muted-foreground))]">·· {a.mask || "----"}</span>
                                                                </div>
                                                                <div className="text-xs text-[hsl(var(--muted-foreground))] capitalize">
                                                                    {a.subtype || a.type}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={`font-semibold tabular-nums ${isCredit ? "text-[#B85A3E] dark:text-[#F9C77E]" : ""}`}>
                                                            {isCredit ? "-" : ""}{fmt(bal || 0)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ConnectedAccounts;
